import React, { ReactElement, useState, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import Modal61 from '@Common/Modal/Modal61';
import InputText from '@Common/InputText';
import { Modal61Ref } from '@Types/common';
import { Tab, ProjectOption } from '../Overview';
import './ConfigModal.scss';

interface ConfigModalProps {
    modalRef: React.RefObject<Modal61Ref> | null;
    tab: Tab;
    availableProjects: Array<ProjectOption>;
    userFavoriteProjects: Array<ProjectOption>;
    isInitialLoad: boolean;
    onMarkInitialLoadComplete: () => void;
    onSave: (tabId: number, tabLabel: string, selectedProjectIds: Array<number>) => Promise<void>;
}

const ConfigModal = (props: ConfigModalProps): ReactElement => {
    const [tabLabel, setTabLabel] = useState<string>(props.tab.label);
    const [availableFilter, setAvailableFilter] = useState<string>('');
    const [selectedFilter, setSelectedFilter] = useState<string>('');
    const [availableProjects, setAvailableProjects] = useState<Array<ProjectOption>>([]);
    const [selectedProjects, setSelectedProjects] = useState<Array<ProjectOption>>([]);

    const onShow = () => {
        // (re)Initialize the projects when modal is shown
        setAvailableProjects(props.availableProjects);

        // Use the selected projects from the tab (already has default selection applied if needed)
        const selectedProjectsFromTab = props.availableProjects.filter((ap) => props.tab.selectedProjectIds?.includes(ap.id));

        setSelectedProjects(selectedProjectsFromTab);
        setTabLabel(props.tab.label);
    };

    const unselectedAvailableProjects = useMemo(() => {
        const filterLower = availableFilter.toLocaleLowerCase();

        return availableProjects
            .filter((x) => !selectedProjects.some((sl) => sl.id === x.id)) // Suppress any projects that have been selected
            .filter((x) => x.name.toLocaleLowerCase().indexOf(filterLower) >= 0)
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }, [availableProjects, selectedProjects, availableFilter]); // Recalculate if any dependency changes

    const renderAvailableProjects = () => {
        return (
            <List
                className='list-group'
                height={350}
                itemCount={unselectedAvailableProjects.length}
                itemSize={50}
                width={'100%'}
                itemData={unselectedAvailableProjects}
                itemKey={(index, data) => {
                    // Find the item at the specified index. "data" is the array of projects that was passed in "itemData"
                    const item = data[index];
                    return item.id;
                }}>
                {({ data, index, style }) => {
                    const item = data[index];

                    return (
                        <div style={style} className='list-group-item' id={item.id.toString()} onClick={() => selectProject(item.id)}>
                            <div className='d-flex w-100 justify-content-between'>
                                <span className='list-item-text align-middle'>{item.name}</span>
                                <div className='list-item-icon'>
                                    <i className='fa-regular fa-plus'></i>
                                </div>
                            </div>
                        </div>
                    );
                }}
            </List>
        );
    };

    const renderSelectedProjects = () => {
        const filterLower = selectedFilter.toLocaleLowerCase();

        const items = selectedProjects.filter((x) => x.name.toLocaleLowerCase().indexOf(filterLower) >= 0).sort((a, b) => (a.name > b.name ? 1 : -1));

        return (
            <List
                className='list-group'
                height={350}
                itemCount={items.length}
                itemSize={50}
                width={'100%'}
                itemData={items}
                itemKey={(index, data) => {
                    // Find the item at the specified index. "data" is the array of projects that was passed in "itemData"
                    const item = data[index];
                    return item.id;
                }}>
                {({ data, index, style }) => {
                    const item = data[index];

                    return (
                        <div
                            style={style}
                            className='list-group-item'
                            key={'selected-loc-' + index}
                            id={item.id.toString()}
                            onClick={() => deselectProject(item.id)}>
                            <div className='d-flex w-100 justify-content-between'>
                                <span className='list-item-text align-middle'>{item.name}</span>
                                <div className='list-item-icon'>
                                    <i className='fa-regular fa-minus'></i>
                                </div>
                            </div>
                        </div>
                    );
                }}
            </List>
        );
    };

    const selectProject = (locId: number) => {
        setAvailableProjects((existing) => existing.filter((x) => x.id !== locId));

        const selected = availableProjects.find((x) => x.id === locId);
        if (selected) {
            setSelectedProjects((existing) => [...existing, selected]);
        }
    };

    const deselectProject = (locId: number) => {
        setSelectedProjects((existing) => existing.filter((x) => x.id !== locId));

        const selected = selectedProjects.find((x) => x.id === locId);
        if (selected) {
            setAvailableProjects((existing) => [...existing, selected]);
        }
    };

    const selectAll = () => {
        setSelectedProjects((existing) => [...existing, ...unselectedAvailableProjects]);
        setAvailableProjects([]);
    };

    const deselectAll = () => {
        setAvailableProjects((existing) => [...existing, ...selectedProjects]);
        setSelectedProjects([]);
    };

    const cleanUp = () => {
        // Clean up or reset data when modal closes

        setAvailableFilter('');
        setSelectedFilter('');
        setAvailableProjects([]);
        setSelectedProjects([]);
        setTabLabel('');
    };

    const handlePrimaryClick = async () => {
        await props.onSave(
            props.tab.id,
            tabLabel,
            selectedProjects.map((project) => project.id)
        );

        cleanUp();
    };

    return (
        <Modal61
            title={`${props.tab.label} Tab Configuration`}
            ref={props.modalRef}
            isCentered={true}
            size='lg'
            onShow={onShow}
            onPrimaryClick={handlePrimaryClick}
            onSecondaryClick={() => cleanUp()}
            onCloseClick={() => cleanUp()}
            body={
                <div className='container-fluid config-modal'>
                    <div className='row'>
                        <div className='col-8'>
                            <div className='form-group'>
                                <label htmlFor='tab-label' className='title'>
                                    Tab Label
                                </label>
                                <InputText
                                    name='tab-label'
                                    type='text'
                                    maxLength={20}
                                    hintText='Character Limit 20'
                                    value={tabLabel}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTabLabel(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='row'>
                        {/* Begin available */}
                        <div className='col-md-6'>
                            <div className='row projects-section-top'>
                                <div className='col-12 my-3'>
                                    <span className='list-title'>Available ({unselectedAvailableProjects.length})</span>
                                </div>
                            </div>

                            <div className='row projects-section'>
                                <div className='col-12 py-2'>
                                    <div className='input-group'>
                                        <input
                                            type='text'
                                            className='form-control'
                                            id='search-available'
                                            placeholder='Search by name'
                                            aria-label='Search by name'
                                            aria-describedby='search-addon'
                                            value={availableFilter}
                                            onChange={(e) => setAvailableFilter(e.target.value)}
                                        />
                                        <span className='input-group-text bg-transparent' id='search-addon'>
                                            <i className='far fa-magnifying-glass'></i>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className='row projects-section'>
                                <div className='col-12 list-container'>{renderAvailableProjects()}</div>
                            </div>

                            <div className='row projects-section-bottom'>
                                <div className='col-12'>
                                    <button type='button' className='btn pacer61-purple-btn' onClick={() => selectAll()}>
                                        Select All
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Begin selected */}
                        <div className='col-md-6'>
                            <div className='row projects-section-top'>
                                <div className='col-9 my-3'>
                                    <span className='list-title'>Selected ({selectedProjects.length})</span>
                                </div>
                            </div>

                            <div className='row projects-section'>
                                <div className='col-12 py-2'>
                                    <div className='input-group'>
                                        <input
                                            type='text'
                                            className='form-control'
                                            id='search-selected'
                                            placeholder='Search by name'
                                            aria-label='Search by name'
                                            aria-describedby='search-addon'
                                            value={selectedFilter}
                                            onChange={(e) => setSelectedFilter(e.target.value)}
                                        />
                                        <span className='input-group-text bg-transparent' id='search-addon'>
                                            <i className='far fa-magnifying-glass'></i>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className='row projects-section'>
                                <div className='col-12 list-container'>{renderSelectedProjects()}</div>
                            </div>

                            <div className='row projects-section-bottom'>
                                <div className='col-12'>
                                    <button type='button' className='btn pacer61-purple-btn' onClick={() => deselectAll()}>
                                        Deselect All
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            primaryButtonText={'Save'}
        />
    );
};

export default ConfigModal;
